import { useEffect, useState } from 'react'
import i18n from '@/i18n'

const REFRESH_INTERVAL_MS = 30_000

function computeRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp
  const totalMinutes = Math.max(0, Math.floor(diffMs / 60_000))
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []

  if (days > 0) {
    parts.push(i18n.t('offline.relativeTime.days', { count: days }))
  }
  if (hours > 0) {
    parts.push(i18n.t('offline.relativeTime.hours', { count: hours }))
  }
  if (minutes > 0 || parts.length === 0) {
    parts.push(i18n.t('offline.relativeTime.minutes', { count: minutes }))
  }

  return i18n.t('offline.relativeTime.ago', { time: parts.join(' ') })
}

export function useRelativeTime(timestamp: number | null): string | null {
  const [text, setText] = useState<string | null>(() =>
    timestamp ? computeRelativeTime(timestamp) : null,
  )

  useEffect(() => {
    if (!timestamp) {
      setText(null)
      return
    }

    setText(computeRelativeTime(timestamp))

    const interval = setInterval(() => {
      setText(computeRelativeTime(timestamp))
    }, REFRESH_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [timestamp])

  useEffect(() => {
    const onLanguageChanged = () => {
      if (timestamp) {
        setText(computeRelativeTime(timestamp))
      }
    }
    i18n.on('languageChanged', onLanguageChanged)
    return () => {
      i18n.off('languageChanged', onLanguageChanged)
    }
  }, [timestamp])

  return text
}
